import { useIntersectionObserver } from '@vueuse/core';
import type { MaybeRefOrGetter } from 'vue';
import { toValue } from 'vue';
import { sportsGtm } from '@/core/lib/gtm';

/** GA labels: K_Lobby_MCard_{view|click}ScorePredIcon_{League|CS}. Named after the feature, not the EPL campaign; full names stay under the 40-char GA4 limit (max 39). */
export type ScorePredictionPromotionGtmLabel = 'ScorePredIcon_League' | 'ScorePredIcon_CS';

// Module scope on purpose: the spec counts each icon once per page load. `v-gtm:@view` dedups per
// DOM element, and the odds display remounts the badge on scroll (VirtualScroll), tab switches
// and More Market toggles, so it would fire again.
const viewedIconIds = new Set<string>();

/**
 * Pushes one GTM `view` event the first time the icon enters the viewport in this page load.
 * `iconId` tells the icons apart (league + live section, or the match of the Correct Score).
 */
export function useScorePredictionPromotionViewTracking(
    target: MaybeRefOrGetter<HTMLElement | null>,
    label: MaybeRefOrGetter<ScorePredictionPromotionGtmLabel>,
    iconId: MaybeRefOrGetter<string>,
): void {
    const { stop } = useIntersectionObserver(target, (entries) => {
        const el = toValue(target);
        if (!el || !entries.some(entry => entry.isIntersecting)) return;

        stop();
        const key = `${toValue(label)}:${toValue(iconId)}`;
        if (viewedIconIds.has(key)) return;

        viewedIconIds.add(key);
        sportsGtm.pushToDataLayer('view', { ...sportsGtm.getGtmEventFromEl(el), label: toValue(label) });
    });
}
